import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { cssGenerator } from '@/mixins/css-generator';
import tooltip from '@/mixins/components/tooltip';
import { SQUASH_DROPLET } from '@/constants';
import DropletOptions from '../_bubble-options';
import GridOptions from './components/_edit-options';
import GridColumn from './components/column';
import { colShape } from './utils';
import schema from './droplet.schema';
const dropletSchema = schema();

export default Vue.component('grid', {
  template: require('./template.html'),
  mixins: [cssGenerator],
  computed: {
    ...mapGetters([
      'currentPage',
      'viewport',
      'localStyle',
      'activeDroplet',
      'activeDropletItem',
      'activeState',
      'animationPreview',
      'dropletObserver',
      'dragging',
      'globalBubbleOptionsOpen',
    ]),
    profile() {
      return this._dropletType(this.droplet);
    },
    hideTransform() {
      return this.globalToolBarOpen ||
      this.globalBubbleOptionsOpen ||
      this.dragging ||
      (this.globalBubbleOpen && !this.showEditBubble);
    },
    getTransformStyle() {
      let style = null;
      if (this.globalToolBarOpen) {
        style = 'initial';
      }
      if (!this.globalBubbleOpen && this.display.perspective) {
        style = 'preserve-3d';
      }
      return style;
    },
    drp() {
      return this.droplet;
    },
    roamingTemplate() {
      return this.currentPage.type !== 'template' && this.droplet.templateRegion;
    },
    isActiveDroplet() {
      return this.activeDroplet._id === this.droplet._id;
    },
    allColumns() {
      const list = [];
      _.forEach(this.droplet.rows, row => _.forEach(row.columns, column => list.push(column)));
      return { list };
    },
    displayStatus() {
      let settings = {
        desktop: 'default',
        tablet: 'default',
        mobile: 'default',
      };
      const selector = _.find(this.droplet.mediaQueries, { selector: ', > .grid-container > .grid-column' });
      if (selector) {
        const property = _.find(selector.properties, { property: 'display' });
        if (property) {
          settings = property;
        }
      }
      return settings;
    },
  },
  data() {
    return {
      type: dropletSchema.type,
      SQUASH_DROPLET,
      display: {
        transform: null,
        perspective: null,
        top: null,
        left: null,
        right: null,
        bottom: null,
        ctPaddingTop: null,
        ctPaddingBottom: null,
        ctPaddingLeft: null,
        ctPaddingRight: null,
        'padding-left': null,
        'padding-right: ': null,
        'padding-top:': null,
        'padding-bottom': null,
        'margin-bottom': null,
        'grid-column-gap': null,
        'grid-row-gap': null,
        'min-height': null,
        'border-top-width': null,
        'border-bottom-width': null,
        'border-left-width': null,
        'border-right-width': null,
        'border-top-right-radius': null,
        'border-top-left-radius': null,
        'border-bottom-right-radius': null,
        'border-bottom-left-radius': null,
        'box-shadow': null,
      },
      hidden: false,
      hasActiveChild: false,
      dropletHovered: false,
      setBubbleSize: 'dropletBubble-wide',
      showEditBubble: false,
      showRevealClass: false,
      activeColumn: null,
    };
  },
  name: 'grid',
  props: [
    'block-index',
    'droplet',
    'items',
    'index',
    'canvas',
    'descendants',
  ],
  components: {
    DropletOptions,
    GridOptions,
    GridColumn,
    tooltip,
  },
  watch: {
    isActiveDroplet(is) {
      if (is) {
        this._scrollToDroplet(this.$el);
      }
    },
    activeState(state) {
      if (state === ':reveal' && this.isActiveDroplet) {
        this.showRevealClass = true;
      } else {
        this.showRevealClass = false;
      }
    },
    animationPreview(should) {
      if (should && this.isActiveDroplet) {
        const droplet = this.$refs.dropletRow;
        const $droplet = $(droplet);
        $droplet.drzOnReveal({
          editing: this.isActiveDroplet,
          child: '> .grid-container > .grid-column',
        });
      }
    },
    viewport() {
      this.callCssGenerator();
      this.observeVisibility();
    },
    'localStyle.value': function setLocals() {
      this.setDisplay();
    },
    'activeDroplet._id': function checkDroplets(id) {
      if (id && this.droplet._id !== id) {
        const activeDroplets = this.$el.querySelectorAll(`#drp-${id}`);
        if (activeDroplets.length > 0) {
          this.hasActiveChild = true;
        }
      } else {
        this.hasActiveChild = false;
      }
      if (!id) {
        this.showRevealClass = false;
      }
    },
    dropletObserver(id) {
      if (this.droplet._id === id) {
        this.observeVisibility();
        this.$store.dispatch('setDropletObserver', false);
      }
    },
  },
  mounted() {
    this.checkColumns();
    this.callCssGenerator();
    this.observeVisibility();
  },
  methods: {
    callCssGenerator() {
      return this.generateCSS({
        drps: [...this.allColumns.list, this.droplet],
      });
    },
    checkColumns() {
      // this should only run when a new grid droplet is first dropped
      if (this.droplet.rows.length < 1) {
        const columns = [];
        _.times(this.droplet.gridColumns, (i) => {
          const column = colShape({
            rowStart: '1',
            colStart: `${i + 1}`,
            rowEnd: '2',
            colEnd: `${i + 2}`,
          })();
          columns.push(column);
        });
        this.$store.dispatch('setDropletVal', {
          drp: this.droplet,
          key: 'rows',
          val: [{ columns }],
        });
      }
    },
    observeVisibility() {
      const display = this.displayStatus;
      if (display[this.viewport] === 'none' || (display[this.viewport] === 'default' && display.desktop === 'none')) {
        this.hidden = true;
      } else {
        this.hidden = false;
      }
    },
    setDisplay() {
      if (this.activeDroplet._id === this.droplet._id) {
        const styles = this.localStyle;
        if (styles.properties.length > 0) {
          _.forEach(styles.properties, (property) => {
            this.$set(this.display, property, styles.value);
          });
        } else {
          _.forOwn(this.display, (value, key) => {
            this.display[key] = null;
          });
        }
      }
    },
    getDisplayShadow(_id) {
      const activeColumn = this.activeDropletItem;
      return (activeColumn && activeColumn._id === _id) || !activeColumn || !activeColumn._id;
    },
    setActiveLink(column) {
      this.activeColumn = column;
    },
    changeBubbleSize(size) {
      this.setBubbleSize = `dropletBubble-${size}`;
    },
    toggleBubble() {
      if (!this.roamingTemplate) {
        this.showEditBubble = !this.showEditBubble;
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/grid/index.js